"use client";

import * as React from "react";
import { useAuth } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAdminRouteSlugs } from "@/features/auth/hooks/use-admin-route-slugs";
import { clearTenantSignOutMarker, isTenantSignOutInProgress } from "@/features/auth/hooks/use-app-sign-out";
import { useTranslations } from "@/i18n";
import { canRetryBootstrap, resolveBootstrapErrorAction } from "@/features/auth/lib/bootstrap-error-action";
import { ACCOUNT_BLOCKED_PATH } from "@/features/auth/lib/system-access";
import {
  TenantAccessDeniedReason,
  type AuthMeResponse,
  type TenantAccessResolveResponse,
} from "@/features/auth/model/auth-types";
import { useAuthContextStore } from "@/features/auth/services/auth-context-store";
import {
  AUTH_BOOTSTRAP_QUERY_KEY,
  AUTH_ME_QUERY_KEY,
  useTenantBootstrapQuery,
} from "@/features/auth/services/auth-query";
import { clearAuthoritativeAuthContext } from "@/features/auth/services/auth-context-sync";
import { recordTenantContext } from "@/features/auth/services/auth-api";
import { clearUserBranchAccessPersist } from "@/features/auth/services/user-branch-access-store";
import { WorkspaceInvitationAwarenessDialog } from "@/features/auth/components/workspace-invitation-awareness-dialog";
import { PushSubscriptionReconciler } from "@/features/push/hooks/use-push-reconcile";
import {
  AUTH_FAILURE_PATH,
  buildLoginRedirectPath,
  collapseAuthFailureNextTarget,
  isAuthAppRoute,
  isPersonalAuthenticatedAppRoute,
  isProtectedAppRoute,
  normalizeTenantPathname,
} from "@/shared/lib/routing/app-route-scope";
import { extractWorkspaceSlugFromAdminPath } from "@/shared/lib/branch/branch-admin-routing";
import { ApiError } from "@/shared/lib/api/http";
import { RouteGuardLoading } from "@ibada/ui/components/route-guard-loading";

type TenantAuthFlowContextValue = {
  clerkLoaded: boolean;
  isAuthenticated: boolean;
  authMe: AuthMeResponse | null;
  authMeResolved: boolean;
  authMeError: Error | null;
  tenantAccess: TenantAccessResolveResponse | null;
  tenantAccessResolved: boolean;
  tenantAccessError: Error | null;
  isLoading: boolean;
};

const TenantAuthFlowContext = React.createContext<TenantAuthFlowContextValue | null>(null);

function buildCurrentTenantHref(pathname: string, search: string) {
  return `${pathname}${search}` || "/";
}

/**
 * How many times the app may check the session by itself before it stops and says so.
 *
 * Three, because the failure this exists to survive is a single blip during startup and a second chance
 * covers it — while an unbounded count against a 60-per-minute bucket produces a 429 the retrying itself
 * caused. Past this the user decides, via Retry.
 */
const MAX_AUTOMATIC_BOOTSTRAP_ATTEMPTS = 3;

function buildTenantAuthFailureHref(next: string) {
  const params = new URLSearchParams({ next: collapseAuthFailureNextTarget(next) });
  return `${AUTH_FAILURE_PATH}?${params.toString()}`;
}

export function TenantAuthFlowProvider({ children }: { children: React.ReactNode }) {
  const t = useTranslations("tenantEntry");
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isLoaded, isSignedIn, userId } = useAuth();
  const { workspaceSlug, branchSlug } = useAdminRouteSlugs();
  const isHandlingUnauthorized = useAuthContextStore((state) => state.isHandlingUnauthorized);
  const setHandlingUnauthorized = useAuthContextStore((state) => state.setHandlingUnauthorized);
  const storedAuthMe = useAuthContextStore((state) => state.authMe);

  const normalizedPathname = normalizeTenantPathname(location.pathname);
  const currentHref = React.useMemo(
    () => buildCurrentTenantHref(normalizedPathname, location.search),
    [location.search, normalizedPathname],
  );
  const isProtectedRoute = isProtectedAppRoute(normalizedPathname);
  const isAuthRoute = isAuthAppRoute(normalizedPathname);
  const isAuthFailureRoute = normalizedPathname === AUTH_FAILURE_PATH;
  const isPersonalAuthRoute = isPersonalAuthenticatedAppRoute(normalizedPathname);
  const isRootRoute = normalizedPathname === "/";
  const isDashboardLandingRoute = normalizedPathname === "/dashboard";
  const isNoAccessRoute = normalizedPathname === "/no-access";
  const isScopedAdminRoute = Boolean(workspaceSlug && extractWorkspaceSlugFromAdminPath(normalizedPathname));
  const needsAuthFlow = isProtectedRoute || isAuthRoute;
  const isSignOutTransition = isTenantSignOutInProgress();
  const noAccessReason = React.useMemo(() => new URLSearchParams(location.search).get("reason"), [location.search]);
  /*
   * The automatic-attempt budget for the session check.
   *
   * It is a COUNT, not a boolean, and spending it DISABLES the query — which is what makes the bound real.
   * A boolean "have I retried yet" was re-armed by ordinary navigation, and every trigger React Query owns
   * (a changed key when the route has no slugs, a remount, a window focus, the invalidate-on-leave below)
   * could then spend another request. Against a 60-per-minute bucket on `/tenant/auth/*` that reaches a 429
   * in seconds, and a 429 answered by retrying never recovers.
   *
   * Once the budget is spent the app stops asking. The user can still press Retry — a person deciding to try
   * again is not a loop, and it is the honest escape hatch when the machine has given up.
   */
  const [bootstrapAttemptsSpent, setBootstrapAttemptsSpent] = React.useState(0);
  const bootstrapBudgetExhausted = bootstrapAttemptsSpent >= MAX_AUTOMATIC_BOOTSTRAP_ATTEMPTS;
  const wasOnAuthFailureRef = React.useRef(false);
  const hasCompletedInitialBootstrapRef = React.useRef(false);
  // Guards the one-time auto-redirect away from `/no-access?reason=tenant-access`: we follow the server's
  // startup landing once so a user who actually has access isn't stranded there (e.g. after a transient
  // denial + reload), but if that landing denies and bounces back here we stop (show the page) instead of
  // ping-ponging. Re-armed after any successful scoped-branch landing.
  const followedTenantAccessLandingRef = React.useRef(false);
  const recordedTenantContextRef = React.useRef<string | null>(null);

  const bootstrapQuery = useTenantBootstrapQuery(workspaceSlug, branchSlug, {
    // Disabling on an exhausted budget is the bound. Anything short of this leaves a refetch trigger able to
    // fire one more request, and "one more" is what a loop is made of.
    enabled: isLoaded && isSignedIn && !isHandlingUnauthorized && !bootstrapBudgetExhausted,
  });
  const refetchBootstrap = bootstrapQuery.refetch;

  React.useEffect(() => {
    if (isAuthFailureRoute) {
      wasOnAuthFailureRef.current = true;
      return;
    }
    if (!wasOnAuthFailureRef.current) return;
    wasOnAuthFailureRef.current = false;
    /*
     * Leaving the failure page is a person choosing to go somewhere, so it earns a fresh budget and a
     * revalidation. This is the deliberate re-arm; there is no other, which is why the count above can only
     * be spent by the app and refilled by the user.
     */
    setBootstrapAttemptsSpent(0);
    void queryClient.invalidateQueries({ queryKey: AUTH_BOOTSTRAP_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
  }, [isAuthFailureRoute, queryClient]);

  const authMe = React.useMemo(() => {
    const candidate = storedAuthMe;
    if (!candidate) return null;
    return candidate.state === undefined ? { ...candidate, state: null } : candidate;
  }, [storedAuthMe]);
  const bootstrap = bootstrapQuery.data ?? null;
  const tenantAccess = bootstrap?.currentContext ?? null;
  // A background authorization refresh must never erase a session we have already resolved. React Query keeps
  // the last successful data when a refetch fails; keep rendering from it while the backend remains the final
  // authority for every subsequent request.
  const hasUsableBootstrap = Boolean(bootstrap && authMe);
  const blockingBootstrapError = hasUsableBootstrap ? null : bootstrapQuery.error;

  React.useEffect(() => {
    if (!isLoaded || !userId) return;
    const persisted = useAuthContextStore.getState().authMe;
    if (persisted && persisted.auth.clerkUserId !== userId) {
      clearAuthoritativeAuthContext();
      clearUserBranchAccessPersist();
    }
  }, [isLoaded, userId]);

  const bootstrapRedirectTarget = React.useMemo(() => {
    if (!isLoaded || !isSignedIn) return null;
    if (!bootstrapQuery.isSuccess || !bootstrap) return null;

    const redirect = bootstrap.startup.redirect;
    if (!redirect) {
      const canonicalLanding = bootstrap.currentContext.canonicalLanding;
      if (
        isDashboardLandingRoute &&
        bootstrap.currentContext.access.allowed &&
        canonicalLanding &&
        currentHref !== canonicalLanding
      ) {
        return canonicalLanding;
      }
      return null;
    }
    if (redirect.kind === "invitation-detail") {
      if (hasCompletedInitialBootstrapRef.current) return null;
      if (normalizedPathname === "/accept-invitation") return null;
      const [pathname, search = ""] = redirect.href.split("?");
      const params = new URLSearchParams(search);
      params.set("returnTo", currentHref);
      return `${pathname}?${params.toString()}`;
    }

    if (isPersonalAuthRoute) return null;

    // Routes that should follow the server-computed startup landing. `/no-access?reason=tenant-access` is
    // included so a user who actually has a valid landing is taken there rather than stranded — but only
    // once (the loop-breaker ref), so a landing that denies and bounces back doesn't ping-pong.
    const canFollowTenantAccessLanding =
      isNoAccessRoute && noAccessReason === "tenant-access" && !followedTenantAccessLandingRef.current;
    const isRedirectableLanding =
      isAuthRoute ||
      isRootRoute ||
      isDashboardLandingRoute ||
      (isNoAccessRoute && noAccessReason !== "tenant-access" && noAccessReason !== "branch-inactive") ||
      normalizedPathname === ACCOUNT_BLOCKED_PATH ||
      canFollowTenantAccessLanding;
    if (!isRedirectableLanding) {
      return null;
    }

    return currentHref === redirect.href ? null : redirect.href;
  }, [
    bootstrap,
    bootstrapQuery.isSuccess,
    currentHref,
    isAuthRoute,
    isDashboardLandingRoute,
    isLoaded,
    isNoAccessRoute,
    isPersonalAuthRoute,
    isRootRoute,
    isSignedIn,
    noAccessReason,
    normalizedPathname,
  ]);

  const tenantResolveRedirectTarget = React.useMemo(() => {
    if (!isScopedAdminRoute || !tenantAccess) return null;

    if (!tenantAccess.access.allowed) {
      const target = tenantAccess.recoveryLanding;
      if (!target) return null;
      return currentHref === target ? null : target;
    }

    return null;
  }, [currentHref, isScopedAdminRoute, tenantAccess]);

  React.useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      hasCompletedInitialBootstrapRef.current = false;
      return;
    }
    if (bootstrapQuery.isSuccess) {
      hasCompletedInitialBootstrapRef.current = true;
    }
  }, [bootstrapQuery.isSuccess, isLoaded, isSignedIn]);

  React.useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      setHandlingUnauthorized(false);
      if (isAuthRoute || isRootRoute) {
        clearTenantSignOutMarker();
      }
      if (isProtectedRoute) {
        const next = isSignOutTransition ? "/login" : buildLoginRedirectPath(normalizedPathname, location.search);
        if (currentHref !== next) {
          navigate(next, { replace: true });
        }
      }
    }
  }, [
    currentHref,
    isLoaded,
    isProtectedRoute,
    isAuthRoute,
    isSignedIn,
    isRootRoute,
    isSignOutTransition,
    location.search,
    navigate,
    normalizedPathname,
    setHandlingUnauthorized,
  ]);

  React.useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    clearAuthoritativeAuthContext();
    clearUserBranchAccessPersist();
    queryClient.removeQueries({ queryKey: AUTH_BOOTSTRAP_QUERY_KEY });
    queryClient.removeQueries({ queryKey: AUTH_ME_QUERY_KEY });
  }, [isLoaded, isSignedIn, queryClient]);

  React.useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setBootstrapAttemptsSpent(0);
      return;
    }

    if (bootstrapQuery.isSuccess) {
      setBootstrapAttemptsSpent(0);
    }
  }, [bootstrapQuery.isSuccess, isLoaded, isSignedIn]);

  React.useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (!bootstrapQuery.isError || isHandlingUnauthorized) return;

    const apiError = bootstrapQuery.error instanceof ApiError ? bootstrapQuery.error : null;
    // Cached authorization may bridge network/5xx/429 revalidation failures, but an explicit 401/403 is an
    // authoritative access verdict and must still follow the normal teardown/redirect path.
    if (hasUsableBootstrap && apiError?.status !== 401 && apiError?.status !== 403) return;
    const action = resolveBootstrapErrorAction(apiError?.status);

    /*
     * Spend one attempt, and only on an error that asking again could actually change. A 401/403 is a settled
     * verdict and a 429 is caused BY asking, so all three go straight to their landing — retrying a rate
     * limit is what turned a slow session check into a permanent one.
     */
    if (
      canRetryBootstrap({
        status: apiError?.status,
        attemptsSpent: bootstrapAttemptsSpent,
        maxAttempts: MAX_AUTOMATIC_BOOTSTRAP_ATTEMPTS
      })
    ) {
      setBootstrapAttemptsSpent((spent) => spent + 1);
      void refetchBootstrap();
      return;
    }

    // Budget spent (or never available): stop asking. `bootstrapBudgetExhausted` disables the query, so this
    // is the last request the app makes on its own.
    if (!bootstrapBudgetExhausted) {
      setBootstrapAttemptsSpent(MAX_AUTOMATIC_BOOTSTRAP_ATTEMPTS);
    }

    // Only a 401 (invalid session) may tear down the authoritative context. A 403 or a 5xx/network/unknown
    // error keeps the session intact, so a transient backend blip can no longer force a logout + redirect loop
    // (prod-incident regression — see resolveBootstrapErrorAction).
    if (action.clearAuthContext) {
      clearAuthoritativeAuthContext();
      clearUserBranchAccessPersist();
    }

    const target =
      action.redirect === "login"
        ? buildLoginRedirectPath(normalizedPathname, location.search)
        : action.redirect === "no-access"
          ? "/no-access?reason=tenant-access"
          : buildTenantAuthFailureHref(currentHref);

    // Idempotent: only navigate if not already on the terminal landing, so the failure state cannot loop.
    if (currentHref !== target) {
      navigate(target, { replace: true });
    }
  }, [
    bootstrapAttemptsSpent,
    bootstrapBudgetExhausted,
    bootstrapQuery.error,
    bootstrapQuery.isError,
    currentHref,
    hasUsableBootstrap,
    isHandlingUnauthorized,
    isLoaded,
    isSignedIn,
    location.search,
    navigate,
    normalizedPathname,
    refetchBootstrap,
  ]);

  React.useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (!bootstrapRedirectTarget) return;
    // Mark that we've spent the one-time follow so a bounce back to tenant-access no-access won't loop.
    if (isNoAccessRoute && noAccessReason === "tenant-access") {
      followedTenantAccessLandingRef.current = true;
    }
    navigate(bootstrapRedirectTarget, { replace: true });
  }, [bootstrapRedirectTarget, isLoaded, isNoAccessRoute, isSignedIn, navigate, noAccessReason]);

  // Re-arm the one-time tenant-access follow after any successful scoped-branch landing, so a later denial
  // can rescue the user again (the guard only suppresses an immediate no-access <-> branch bounce).
  React.useEffect(() => {
    if (isScopedAdminRoute && tenantAccess?.access.allowed) {
      followedTenantAccessLandingRef.current = false;
    }
  }, [isScopedAdminRoute, tenantAccess]);

  React.useEffect(() => {
    if (!isScopedAdminRoute || !tenantAccess?.access.allowed) return;
    const workspaceId = tenantAccess.resolved.workspace?.id;
    const branchId = tenantAccess.resolved.branch?.id;
    if (!workspaceId || !branchId) return;
    const key = `${workspaceId}:${branchId}`;
    if (recordedTenantContextRef.current === key) return;
    recordedTenantContextRef.current = key;
    void recordTenantContext({ workspaceId, branchId }).catch(() => {
      recordedTenantContextRef.current = null;
    });
  }, [isScopedAdminRoute, tenantAccess]);

  React.useEffect(() => {
    if (!tenantAccess?.invalidRequestReason) return;

    const toastKey = `tenant-access:workspace-not-accessible:${currentHref}`;
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(toastKey)) return;

    window.sessionStorage.setItem(toastKey, "1");
    toast.error(t("notFound.title"), {
      description: t("notFound.description"),
      duration: Infinity,
    });
  }, [currentHref, t, tenantAccess]);

  React.useEffect(() => {
    if (!isScopedAdminRoute || !tenantAccess) return;

    if (!tenantAccess.access.allowed && !tenantResolveRedirectTarget) {
      const noAccessHref = tenantAccess.access.reason === TenantAccessDeniedReason.WORKSPACE_NOT_ACCESSIBLE
        ? "/no-access?reason=tenant-access&detail=workspace-not-found"
        : "/no-access?reason=tenant-access";
      if (!isNoAccessRoute || noAccessReason !== "tenant-access") {
        navigate(noAccessHref, { replace: true });
      }
      return;
    }

    if (tenantResolveRedirectTarget) {
      navigate(tenantResolveRedirectTarget, { replace: true });
    }
  }, [
    isNoAccessRoute,
    isScopedAdminRoute,
    navigate,
    noAccessReason,
    tenantAccess,
    tenantResolveRedirectTarget,
  ]);

  const isResolvingAuthenticatedLoginRoute = Boolean(
    isAuthRoute &&
      !isAuthFailureRoute &&
      (!isLoaded ||
        (isSignedIn &&
          (bootstrapQuery.fetchStatus === "idle" ||
            bootstrapQuery.isPending ||
            bootstrapQuery.isLoading ||
            !hasUsableBootstrap ||
            Boolean(bootstrapRedirectTarget)))),
  );

  const isLoading =
    (!isLoaded && needsAuthFlow) ||
    (isSignedIn &&
      isProtectedRoute &&
      !hasUsableBootstrap &&
      (bootstrapQuery.isLoading || (bootstrapQuery.isError && !isAuthFailureRoute))) ||
    isResolvingAuthenticatedLoginRoute ||
    Boolean(bootstrapRedirectTarget);

  const contextValue = React.useMemo<TenantAuthFlowContextValue>(
    () => ({
      clerkLoaded: isLoaded,
      isAuthenticated: Boolean(isSignedIn),
      authMe: authMe ?? null,
      authMeResolved: hasUsableBootstrap,
      /*
       * The real error, not a hardcoded null.
       *
       * `/auth/failure` decides between its card and a spinner with
       * `stillChecking = isAuthenticated && !authMeResolved && !authMeError`. With this pinned to null the
       * middle term could never be satisfied while a check was failing, so the page showed a spinner
       * forever — and its Retry button, the one way out once the automatic budget is spent, was unreachable
       * behind it.
       */
      authMeError: blockingBootstrapError ?? null,
      tenantAccess,
      tenantAccessResolved: Boolean(hasUsableBootstrap && tenantAccess),
      tenantAccessError: blockingBootstrapError ?? null,
      isLoading,
    }),
    [
      authMe,
      blockingBootstrapError,
      hasUsableBootstrap,
      isLoaded,
      isLoading,
      isSignedIn,
      tenantAccess,
    ],
  );

  return (
    <TenantAuthFlowContext.Provider value={contextValue}>
      {children}
      <WorkspaceInvitationAwarenessDialog />
      <PushSubscriptionReconciler />
    </TenantAuthFlowContext.Provider>
  );
}

export function TenantAuthLoadingGate({ children }: { children: React.ReactNode }) {
  const state = useTenantAuthFlow();

  if (state.isLoading) {
    return <RouteGuardLoading />;
  }

  return <>{children}</>;
}

export function useTenantAuthFlow() {
  const context = React.useContext(TenantAuthFlowContext);
  const { isLoaded, isSignedIn, userId } = useAuth();
  const fallbackAuthMe = useAuthContextStore((state) => state.authMe);
  const fallbackAuthMeStatus = useAuthContextStore((state) => state.authMeStatus);
  const fallbackAuthMeFetchStatus = useAuthContextStore((state) => state.authMeFetchStatus);

  if (context) {
    return context;
  }

  const safeFallbackAuthMe =
    fallbackAuthMe && userId && fallbackAuthMe.auth.clerkUserId === userId ? fallbackAuthMe : null;

  return {
    clerkLoaded: isLoaded,
    isAuthenticated: Boolean(isSignedIn),
    authMe: safeFallbackAuthMe,
    authMeResolved:
      fallbackAuthMeStatus === "success" || fallbackAuthMeStatus === "error" || Boolean(safeFallbackAuthMe),
    authMeError: null,
    tenantAccess: null,
    tenantAccessResolved: false,
    tenantAccessError: null,
    isLoading:
      !isLoaded ||
      (Boolean(isSignedIn) && fallbackAuthMeFetchStatus === "fetching") ||
      (Boolean(isSignedIn) && fallbackAuthMeStatus === "pending"),
  } satisfies TenantAuthFlowContextValue;
}
